// Client-side CSV builder in the **Excel fr-BE dialect**: `;` separator, decimal comma (`18,15`), UTF-8 BOM,
// CRLF line ends — what a Belgian Excel opens directly, with amounts parsed as numbers. Pure functions; the
// download itself goes through utils/fileDownload. (The Wallets ledger export keeps its own RFC 4180 `,`
// dialect in Admin/Wallets/ledgerCsv.ts.)
//
// Columns are typed so that formula-injection neutralization applies to TEXT only: a text value starting
// with `=`, `+`, `-`, `@`, tab or CR is prefixed with `'` (it may come from a tenant or an upstream), while
// an amount is never touched — its negatives legitimately start with `-`.

/** UTF-8 byte-order mark so Excel reads the file as UTF-8 (accents, `—`). */
export const EXCEL_CSV_BOM = '﻿';

/** `text` is neutralized; `amount` gets a decimal comma; `number` (integers, counts, ids) and `date` (already formatted) pass as is. */
export type ExcelCsvKind = 'text' | 'amount' | 'number' | 'date';

export interface ExcelCsvColumn<T> {
  header: string;
  kind: ExcelCsvKind;
  value: (row: T) => string | number | boolean | null | undefined;
}

const FORMULA_TRIGGER_RE = /^[=+\-@\t\r]/;
const DECIMAL_RE = /^[+-]?\d+(\.\d+)?$/;

/** Prefixes `'` to a text value that a spreadsheet would read as a formula. */
export function neutralizeFormula(value: string): string {
  return FORMULA_TRIGGER_RE.test(value) ? `'${value}` : value;
}

/**
 * A decimal money string with a decimal comma, by string manipulation only — no float round trip, so
 * Mollie's 5-decimal amounts keep every digit (`"-0.16335"` → `"-0,16335"`). Anything that is not a plain
 * decimal is treated as text (neutralized) rather than trusted as a number.
 */
export function excelAmount(value: string): string {
  const trimmed = value.trim();
  return DECIMAL_RE.test(trimmed) ? trimmed.replace('.', ',') : neutralizeFormula(value);
}

/** Quotes a field containing the `;` separator, a double quote or a line break (embedded quotes doubled). */
export function excelCsvField(value: string): string {
  return /[;"\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

function cell(kind: ExcelCsvKind, raw: string | number | boolean | null | undefined): string {
  if (raw === null || raw === undefined) {
    return '';
  }
  if (typeof raw === 'boolean') {
    return raw ? 'Oui' : 'Non';
  }
  const s = String(raw);
  switch (kind) {
    case 'amount':
      return excelCsvField(excelAmount(s));
    case 'text':
      return excelCsvField(neutralizeFormula(s));
    default:
      return excelCsvField(s);
  }
}

/** Whole file: BOM + header + one line per row, each CRLF-terminated. */
export function buildExcelCsv<T>(columns: ExcelCsvColumn<T>[], rows: T[]): string {
  const header = columns.map((c) => excelCsvField(c.header)).join(';');
  const lines = rows.map((row) => columns.map((c) => cell(c.kind, c.value(row))).join(';'));
  return `${EXCEL_CSV_BOM}${[header, ...lines].join('\r\n')}\r\n`;
}
